import api from "../auth/api";
import type {
  PricingPlanWithFeatures,
  CreatePricingPlanRequest,
  UpdatePricingPlanRequest,
  UpdatePricingPlanPositionRequest,
  UpdatePricingPlanFeaturesRequest,
  Feature,
  CreateFeatureRequest,
  UpdateFeatureRequest,
  UpdateFeatureOrderRequest,
  GetPricingPlanHistoryQuery,
  PricingPlanHistoryResponse,
  PaymentInfoResponse,
  BuyPricingPlanRequest,
} from "./pricing-plan.type";

// Pricing Plans API
export const getPricingPlans = async (): Promise<PricingPlanWithFeatures[]> => {
  const response = await api.get<{
    data: PricingPlanWithFeatures[];
    success: boolean;
    message: string;
    error: string;
  }>("/pricing-plans");
  return response.data.data;
};

export const getPricingPlan = async (
  id: number
): Promise<PricingPlanWithFeatures> => {
  const response = await api.get<{
    data: PricingPlanWithFeatures;
    success: boolean;
    message: string;
    error: string;
  }>(`/pricing-plans/${id}`);
  return response.data.data;
};

export const createPricingPlan = async (
  data: CreatePricingPlanRequest
): Promise<PricingPlanWithFeatures> => {
  const response = await api.post<{
    data: PricingPlanWithFeatures;
    success: boolean;
    message: string;
    error: string;
  }>("/pricing-plans", data);
  return response.data.data;
};

export const updatePricingPlan = async (
  id: number,
  data: UpdatePricingPlanRequest
): Promise<PricingPlanWithFeatures> => {
  const response = await api.put<{
    data: PricingPlanWithFeatures;
    success: boolean;
    message: string;
    error: string;
  }>(`/pricing-plans/${id}`, data);
  return response.data.data;
};

export const updatePricingPlanPosition = async (
  id: number,
  data: UpdatePricingPlanPositionRequest
): Promise<void> => {
  await api.patch(`/pricing-plans/${id}/positions`, data);
};

export const updatePricingPlanFeatures = async (
  id: number,
  data: UpdatePricingPlanFeaturesRequest
): Promise<void> => {
  await api.put(`/pricing-plans/${id}/features`, data);
};

export const deletePricingPlan = async (id: number): Promise<void> => {
  await api.delete(`/pricing-plans/${id}`);
};

// Features API
export const getFeatures = async (): Promise<Feature[]> => {
  const response = await api.get<{
    data: Feature[];
    success: boolean;
    message: string;
    error: string;
  }>("/features");
  return response.data.data;
};

export const getFeature = async (key: string): Promise<Feature> => {
  const response = await api.get<{
    data: Feature;
    success: boolean;
    message: string;
    error: string;
  }>(`/features/${key}`);
  return response.data.data;
};

export const createFeature = async (
  data: CreateFeatureRequest
): Promise<Feature> => {
  const response = await api.post<{
    data: Feature;
    success: boolean;
    message: string;
    error: string;
  }>("/features", data);
  return response.data.data;
};

export const updateFeature = async (
  key: string,
  data: UpdateFeatureRequest
): Promise<Feature> => {
  const response = await api.put<{
    data: Feature;
    success: boolean;
    message: string;
    error: string;
  }>(`/features/${key}`, data);
  return response.data.data;
};

export const updateFeatureOrder = async (
  key: string,
  data: UpdateFeatureOrderRequest
): Promise<void> => {
  await api.patch(`/features/${key}/order`, data);
};

export const deleteFeature = async (key: string): Promise<void> => {
  await api.delete(`/features/${key}`);
};

// Subscription API

/** Envelope returned by the buy/assign/cancel/resume endpoints. Callers only
 * read success/message; `data` varies by endpoint and is not typed yet. */
type SubscriptionActionResponse = {
  data: unknown;
  success: boolean;
  message: string;
  error: string;
};

export const buyPricingPlan = async (data: BuyPricingPlanRequest): Promise<SubscriptionActionResponse> => {
  const response = await api.post<SubscriptionActionResponse>("/pricing-plans/buy", data);
  return response.data;
};

/** Charge a past-due subscription now. Throws (HTTP 400) when the card is declined again. */
export const retryFailedPayment = async (): Promise<SubscriptionActionResponse> => {
  const response = await api.post<SubscriptionActionResponse>("/pricing-plans/retry-payment");
  return response.data;
};

export const assignPricingPlan = async (data: {
  pricing_plan_id: number;
  user_id: string;
}): Promise<SubscriptionActionResponse> => {
  const response = await api.post<SubscriptionActionResponse>("/pricing-plans/assign", data);
  return response.data;
};

export const cancelPricingPlan = async (data: {
  user_id: string;
}): Promise<SubscriptionActionResponse> => {
  const response = await api.post<SubscriptionActionResponse>("/pricing-plans/cancel", data);
  return response.data;
};

export const resumePricingPlan = async (data: {
  user_id: string;
}): Promise<SubscriptionActionResponse> => {
  const response = await api.post<SubscriptionActionResponse>("/pricing-plans/resume", data);
  return response.data;
};

// Get pricing plan history
export const getPricingPlanHistory = async (
  query: GetPricingPlanHistoryQuery = {}
): Promise<PricingPlanHistoryResponse> => {
  const response = await api.get<PricingPlanHistoryResponse>(
    "/pricing-plans/history",
    {
      params: query,
    }
  );
  return response.data;
};

// Get payment info
export const getPaymentInfo = async (): Promise<PaymentInfoResponse["data"]> => {
  const response = await api.get<PaymentInfoResponse>(
    "/pricing-plans/payment-info"
  );
  return response.data.data;
};

